import {
  Button,
  Checkbox,
  Dropdown,
  Field,
  makeStyles,
  Option,
  Spinner,
} from "@fluentui/react-components";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGetFilterableFields } from "../../hooks/useSearchConfiguration";
import { Field as FieldModel } from "../../models/Field";
import {
  SearchFilter,
  SearchFilterConfiguration,
  SearchFilterValue,
} from "../../models/SearchConfiguration";
import { FlatVsaaTag } from "../../models/VsaaTag";
import { VsaaTagPicker } from "../tagpicker/VsaaTagPicker";

const useClasses = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  fieldLabel: {
    fontWeight: 600,
  },
});

interface SearchConfigurationPickerProps {
  configuration: SearchFilterConfiguration;
  allowChaptersFilter?: boolean;
  onConfigurationChange: (newConfiguration: SearchFilterConfiguration) => void;
  dropdownLabel?: string;
  dropdownHint?: string;
}

export const SearchConfigurationPicker = (
  props: SearchConfigurationPickerProps
) => {
  const classes = useClasses();
  const { configuration, allowChaptersFilter, onConfigurationChange } = props;
  const [currentConfiguration, setCurrentConfiguration] =
    useState<SearchFilterConfiguration>(configuration);
  const [selectedFilterField, setSelectedFilterField] = useState<string>("");
  const [tagPickerValues, setTagPickerValues] = useState<
    Record<string, FlatVsaaTag[]>
  >({});

  const { data: fields, isLoading: fieldsLoading } = useGetFilterableFields();
  const { t } = useTranslation();

  const mapFilterValueToTag = (
    value: SearchFilterValue,
    tagCollectionId: string
  ): FlatVsaaTag => {
    return {
      id: value.value,
      title: value.displayValue,
      tagCollectionId: tagCollectionId,
      path: "",
    };
  };

  const mapFilterValuesToTags = useCallback(
    (filter: SearchFilter, field: FieldModel): FlatVsaaTag[] => {
      const tags = filter.values.map((val) =>
        mapFilterValueToTag(val, field.tagCollectionId as string)
      );

      return tags;
    },
    []
  );

  const mapTagToFilterValue = (tag: FlatVsaaTag): SearchFilterValue => {
    return {
      value: tag.id,
      displayValue: tag.title,
    };
  };

  useEffect(() => {
    if (!fields || fieldsLoading || !configuration.filters) {
      return;
    }

    const newTagPickerValues = structuredClone(tagPickerValues);
    let hasChangeNewField = false;

    fields.forEach((f) => {
      if (!newTagPickerValues[f.uniqueName]) {
        const existingFilter = configuration.filters.find(
          (filter) => filter.fieldUniqueName === f.uniqueName
        );

        newTagPickerValues[f.uniqueName] =
          existingFilter && existingFilter.values
            ? mapFilterValuesToTags(existingFilter, f)
            : [];
        hasChangeNewField = true;
      }
    });

    if (hasChangeNewField) {
      /* eslint-disable-next-line react-hooks/set-state-in-effect */
      setTagPickerValues(newTagPickerValues);
    }
  }, [
    fields,
    configuration,
    tagPickerValues,
    fieldsLoading,
    mapFilterValuesToTags,
  ]);

  if (!fields || fieldsLoading) {
    return <Spinner />;
  }

  const getField = (fieldUniqueName: string) => {
    const field = fields.find((f) => f.uniqueName === fieldUniqueName);

    return field;
  };

  const handleEnableFiltersOnChaptersChecked = (checked: boolean) => {
    const newSearchConfig = structuredClone(currentConfiguration);
    newSearchConfig.enableFilterOnChapters = checked;

    setCurrentConfiguration(newSearchConfig);

    onPickerChange(newSearchConfig, tagPickerValues);
  };

  const onAddFilterClick = () => {
    const field = getField(selectedFilterField);

    // Filter on non tag field currently not supported.
    if (!field || !field.tagCollectionId) {
      return;
    }

    if (
      // No duplicate filters.
      currentConfiguration.filters.find(
        (f) => f.fieldUniqueName === field.uniqueName
      )
    ) {
      return;
    }

    const newSearchConfig = structuredClone(currentConfiguration);

    if (!newSearchConfig.filters) {
      newSearchConfig.filters = [];
    }

    const searchFilter: SearchFilter = {
      fieldUniqueName: field.uniqueName,
      fieldDisplayName: field.displayName,
      values: [],
      includeAllOptions: false,
    };

    newSearchConfig.filters.push(searchFilter);
    setCurrentConfiguration(newSearchConfig);
  };

  const onPickerChange = (
    configuration: SearchFilterConfiguration,
    tagValues: Record<string, FlatVsaaTag[]>
  ) => {
    const newFilters: SearchFilter[] = [];

    const configurationToSave = structuredClone(configuration);
    configurationToSave.filters.forEach((configuredFilter) => {
      const key = configuredFilter.fieldUniqueName;
      if (tagValues[key].length > 0) {
        const field = fields.find((f) => f.uniqueName === key);

        if (field) {
          const filterValues = tagValues[key].map(mapTagToFilterValue);
          const filter: SearchFilter = {
            fieldUniqueName: field.uniqueName,
            fieldDisplayName: field.displayName,
            values: filterValues,
            includeAllOptions: false,
          };

          newFilters.push(filter);
        }
      } else {
        const currentFilter = configurationToSave.filters.find(
          (f) => f.fieldUniqueName === key
        );

        if (currentFilter && currentFilter.includeAllOptions) {
          const field = fields.find((f) => f.uniqueName === key);

          if (field) {
            const filter: SearchFilter = {
              fieldUniqueName: field.uniqueName,
              fieldDisplayName: field.displayName,
              values: [],
              includeAllOptions: true,
            };

            newFilters.push(filter);
          }
        }
      }
    });

    configurationToSave.filters = newFilters;

    if (!allowChaptersFilter) {
      // This should not be needed if chapter filters are disallowed set it explicitly to false.
      configurationToSave.enableFilterOnChapters = false;
    }

    onConfigurationChange(configurationToSave);
  };

  const onIncludeAllOptionChange = (fieldName: string) => {
    const configurationToSave = structuredClone(currentConfiguration);
    configurationToSave.filters.forEach((filter) => {
      if (filter.fieldUniqueName === fieldName) {
        filter.includeAllOptions = !filter.includeAllOptions;
        return;
      }
    });

    setCurrentConfiguration(configurationToSave);
    onPickerChange(configurationToSave, tagPickerValues);
  };

  const updateTagPickerValues = (
    tags: FlatVsaaTag[],
    fieldUniqueName: string
  ) => {
    const newTagPickerValues = structuredClone(tagPickerValues);

    newTagPickerValues[fieldUniqueName] = tags;

    setTagPickerValues(newTagPickerValues);

    onPickerChange(currentConfiguration, newTagPickerValues);
  };

  return (
    <div className={classes.root}>
      {allowChaptersFilter && (
        <Checkbox
          checked={currentConfiguration.enableFilterOnChapters}
          onChange={(_ev, data) =>
            handleEnableFiltersOnChaptersChecked(data.checked as boolean)
          }
          label={t("searchFilterPicker.includeChapters")}
        />
      )}
      <Field
        label={
          props.dropdownLabel && (
            <span className={classes.fieldLabel}>{props.dropdownLabel}</span>
          )
        }
        hint={props.dropdownHint}
      >
        <Dropdown
          selectedOptions={[selectedFilterField]}
          placeholder={t("searchFilterPicker.addFilterDropdownPlaceholder")}
          onOptionSelect={(_ev, data) =>
            setSelectedFilterField(data.optionValue as string)
          }
        >
          {fields.map((field) => (
            <Option key={field.uniqueName} value={field.uniqueName}>
              {field.displayName}
            </Option>
          ))}
        </Dropdown>
      </Field>
      <Button disabled={!selectedFilterField} onClick={onAddFilterClick}>
        {t("searchFilterPicker.addFilter")}
      </Button>

      {currentConfiguration.filters &&
        currentConfiguration.filters.map((filter) => {
          const field = getField(filter.fieldUniqueName);

          if (!field || !field.tagCollectionId) {
            return null;
          }

          return (
            <div key={filter.fieldUniqueName}>
              <VsaaTagPicker
                label={field.displayName}
                tagCollectionId={field.tagCollectionId}
                multiValue
                value={tagPickerValues[field.uniqueName]}
                onChange={(t) => updateTagPickerValues(t, field.uniqueName)}
              />
              <Checkbox
                checked={filter.includeAllOptions}
                onChange={() => onIncludeAllOptionChange(field.uniqueName)}
                label={t("searchFilterPicker.includeAllOptions")}
              />
            </div>
          );
        })}
    </div>
  );
};
